import { wordsApi } from '@/lib/api';
import type { NoyauCode, NoyauKey, Word } from '@/schemas/word';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const NOYAU_OPTIONS: { code: NoyauCode; key: NoyauKey; label: string }[] = [
  { code: 'SGP', key: 'bleunuit', label: 'SGP' },
  { code: 'SPG', key: 'bleuciel', label: 'SPG' },
  { code: 'PSG', key: 'vert', label: 'PSG' },
  { code: 'PGS', key: 'jaune', label: 'PGS' },
  { code: 'GPS', key: 'rouge', label: 'GPS' },
  { code: 'GSP', key: 'rose', label: 'GSP' },
];

interface CreateWordModalProps {
  selectedText: string;
  onConfirm: (word: Word) => void;
  onClose: () => void;
}

/**
 * Dialog for creating a stub word (no blocks). Reused wherever a word can be
 * created on the fly: the editor toolbar (from selected text), the "Voir aussi"
 * autocomplete (when the typed word doesn't exist), and the sidebar "Nouveau"
 * button (empty `selectedText`). On success it hands the created word back via
 * `onConfirm` so the caller can link to or navigate to it.
 */
export function CreateWordModal({ selectedText, onConfirm, onClose }: CreateWordModalProps) {
  const [title, setTitle] = useState(selectedText);
  const [noyauCode, setNoyauCode] = useState<NoyauCode | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const createMutation = useMutation({
    mutationFn: () => wordsApi.create({ title: title.trim(), noyauCode }),
    onSuccess: (word) => onConfirm(word),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    createMutation.mutate();
  }

  const modal = (
    <dialog
      open
      className="cwm-overlay"
      aria-modal="true"
      aria-label="Créer un nouveau mot"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cwm">
        <p className="cwm__hint">
          {selectedText
            ? `« ${selectedText} » n'existe pas encore dans le dictionnaire.`
            : 'Créer un nouveau mot dans le dictionnaire.'}
        </p>
        <form onSubmit={handleSubmit}>
          <label className="cwm__label" htmlFor="cwm-title">
            Titre
          </label>
          <input
            ref={inputRef}
            id="cwm-title"
            className="cwm__input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />

          <p className="cwm__label">Noyau (optionnel)</p>
          <div className="cwm__noyau-row">
            {NOYAU_OPTIONS.map((opt) => (
              <button
                key={opt.code}
                type="button"
                className={`cwm__noyau-btn${noyauCode === opt.code ? ' is-selected' : ''}`}
                aria-label={opt.code}
                aria-pressed={noyauCode === opt.code}
                style={
                  {
                    '--noyau-color': `var(--color-pms-${opt.key.replace('bleunuit', 'bleu-nuit').replace('bleuciel', 'bleu-ciel')})`,
                  } as React.CSSProperties
                }
                onClick={() => setNoyauCode(noyauCode === opt.code ? null : opt.code)}
              >
                <span className="cwm__noyau-dot" />
                <span className="cwm__noyau-code">{opt.code}</span>
              </button>
            ))}
          </div>

          {createMutation.isError && (
            <p className="cwm__error">Erreur lors de la création. Réessayez.</p>
          )}

          <div className="cwm__actions">
            <button type="button" className="cwm__btn cwm__btn--cancel" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="cwm__btn cwm__btn--create"
              disabled={!title.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Création…' : 'Créer le mot'}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );

  return createPortal(modal, document.body);
}
